import { createContext } from 'react'

import { SaveableAssetMapContext } from '@/pages/assets/map/MapContext'
import type {
  DashboardType,
  HistoryGraphSettings,
  InsightConfigurationType,
  InsightDefinition,
  InsightParametersType,
  ProfileType,
  SavedView,
  SavedViewPage,
} from '@/types/dashboard-types'

export interface DashboardContextType {
  configuration: ProfileType
  currentDashboard: DashboardType
  setCurrentDashboardSlug: (slug: string) => void
  setDefaultDashboard: (slug: string) => void
  addDashboard: ({
    dashboard,
    insightConfiguration,
    insights,
  }: {
    dashboard: DashboardType
    insightConfiguration?: InsightConfigurationType
    insights?: InsightConfigurationType[]
  }) => Promise<DashboardType>
  removeDashboard: ({ slug }: { slug: string }) => void
  reorderDashboards: (dashboards: DashboardType[]) => void
  getInsightConfiguration: ({ dashboardSlug, id }: { dashboardSlug: string; id: string }) => InsightConfigurationType
  addInsight: ({
    dashboardSlug,
    insightConfiguration,
  }: {
    dashboardSlug: string
    insightConfiguration: InsightConfigurationType
  }) => void
  removeInsight: ({ id }: { id: string }) => void
  updateInsight: ({
    dashboardSlug,
    id,
    insightConfiguration,
  }: {
    dashboardSlug: string
    id: string
    insightConfiguration: InsightConfigurationType
  }) => void
  updateDashboardParameters: (parameters: InsightParametersType) => void
  buildInsight: <P, D>(insightDefinition: InsightDefinition<P, D>) => InsightConfigurationType
  sync: () => void
  error?: string
  isLoading?: boolean
  saveAssetMap: (assetMap: SaveableAssetMapContext) => void
  deleteAssetMap: (name: string) => void
  /** Saved views on the other list pages, stored in the profile's savedViews. */
  saveView: (page: SavedViewPage, view: SavedView) => void
  deleteView: (page: SavedViewPage, name: string) => void
  /** Temperature Graph layout, stored in the profile's historyGraph. Optional because the
   *  graph also renders outside the provider, where the context is its bare default. */
  saveHistoryGraph?: (settings: HistoryGraphSettings) => void
}

// Context in separate file to prevent HMR from creating new context instances
export const DashboardContext = createContext<DashboardContextType>({ isLoading: true } as DashboardContextType)
